import {
  type ApiOutputs,
  visitDetailSchema,
  visitListSchema,
} from "@rosc/contracts";
import { checkPublicPage } from "@/lib/public-pagination";

type Summary = ApiOutputs["listVisits"]["items"][number];
export function parseVisitPage(
  value: unknown,
  cursor?: string,
): ApiOutputs["listVisits"] {
  return checkPublicPage(visitListSchema.parse(value), cursor);
}
export function parseVisitDetail(
  value: unknown,
  requestedId: string,
): ApiOutputs["getVisit"] {
  const detail = visitDetailSchema.parse(value);
  if (detail.public_id !== requestedId)
    throw new Error("Unexpected visit detail");
  return detail;
}
export type VisitSummary = Omit<
  Summary,
  "rowNumber" | "durationSeconds" | "createdAt" | "customTemplatePublicId"
> & {
  rowno: Summary["rowNumber"];
  duration: Summary["durationSeconds"];
  createDate: Summary["createdAt"];
  customTemplatePublicId: Summary["customTemplatePublicId"];
};
export type VisitDetail = VisitSummary &
  Pick<
    ApiOutputs["getVisit"],
    | "name"
    | "awsBucket"
    | "transcription"
    | "result"
    | "original"
    | "customTemplateName"
    | "versions"
  >;
export function toVisitSummary(visit: Summary): VisitSummary {
  const {
    rowNumber,
    durationSeconds,
    createdAt,
    customTemplatePublicId,
    ...rest
  } = visit;
  return {
    ...rest,
    rowno: rowNumber,
    duration: durationSeconds,
    createDate: createdAt,
    customTemplatePublicId: customTemplatePublicId,
  };
}
export function toVisitDetail(visit: ApiOutputs["getVisit"]): VisitDetail {
  return {
    ...toVisitSummary(visit),
    name: visit.name,
    awsBucket: visit.awsBucket,
    transcription: visit.transcription,
    result: visit.result,
    original: visit.original,
    customTemplateName: visit.customTemplateName,
    versions: visit.versions,
  };
}
export function sortVisitSummaries(items: Summary[]): VisitSummary[] {
  return items
    .map(toVisitSummary)
    .sort((a, b) =>
      a.rowno === null
        ? b.rowno === null
          ? 0
          : 1
        : b.rowno === null
          ? -1
          : b.rowno - a.rowno,
    );
}
